/**
 * WebSocket сервис для real-time обновлений
 */

import { io, Socket } from 'socket.io-client';

class WebSocketService {
  private socket: Socket | null = null;
  private listeners: Map<string, Set<Function>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = Infinity; // Бесконечные попытки переподключения
  private reconnectDelay = 1000; // Начальная задержка 1 сек
  private currentToken: string | null = null;
  private manualDisconnect = false;
  private reconnectTimer: NodeJS.Timeout | null = null;

  /**
   * Подключение к WebSocket серверу
   */
  connect(token: string): void {
    if (this.socket?.connected) {
      console.log('WebSocket уже подключен');
      return;
    }

    // Сохраняем токен для переподключения
    this.currentToken = token;
    this.manualDisconnect = false;

    // Очищаем предыдущий таймер переподключения
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Если сокет существует но отключен, сначала закрываем его
    if (this.socket && !this.socket.connected) {
      this.socket.removeAllListeners();
      this.socket.close();
      this.socket = null;
    }

    const wsUrl = process.env.REACT_APP_WS_URL || window.location.protocol === 'https:' ? 'wss://' + window.location.host : 'ws://localhost:5000';

    this.socket = io(wsUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
    });

    this.setupEventHandlers();
  }

  /**
   * Настройка обработчиков событий
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    // Подключение установлено
    this.socket.on('connect', () => {
      console.log('✅ WebSocket подключен');
      this.reconnectAttempts = 0;

      // Очищаем таймер переподключения при успешном подключении
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }

      this.emit('connected', true);
    });

    // Отключение
    this.socket.on('disconnect', (reason) => {
      console.log('❌ WebSocket отключен:', reason);
      this.emit('connected', false);

      // Автоматическое переподключение, если отключение не было вручную
      if (!this.manualDisconnect && this.currentToken) {
        this.scheduleReconnect();
      }
    });

    // Ошибка подключения
    this.socket.on('connect_error', (error) => {
      console.error('WebSocket ошибка подключения:', error.message);
      this.reconnectAttempts++;
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('Превышено количество попыток переподключения');
        this.emit('connection_failed', error);
      }
    });

    // Обновление баланса
    this.socket.on('balance_update', (data) => {
      console.log('💰 Обновление баланса:', data);
      this.emit('balance_update', data);
    });

    // Обновление стейкинга
    this.socket.on('staking_update', (data) => {
      console.log('📊 Обновление стейкинга:', data);
      this.emit('staking_update', data);
    });

    // Новое сообщение в чате
    this.socket.on('chat_message', (data) => {
      console.log('💬 Новое сообщение:', data);
      this.emit('chat_message', data);
    });

    // Обновление множителя
    this.socket.on('multiplier_activated', (data) => {
      console.log('🎯 Множитель активирован:', data);
      this.emit('multiplier_activated', data);
    });

    // Мини-игра доступна
    this.socket.on('minigame_available', (data) => {
      console.log('🎮 Мини-игра доступна:', data);
      this.emit('minigame_available', data);
    });

    // ❌ УДАЛЕНО: Сезоны
    // this.socket.on('season_update', (data) => {
    //   console.log('🏆 Обновление сезона:', data);
    //   this.emit('season_update', data);
    // });

    // Реферальное событие
    this.socket.on('referral_event', (data) => {
      console.log('👥 Реферальное событие:', data);
      this.emit('referral_event', data);
    });

    // Системное уведомление
    this.socket.on('system_notification', (data) => {
      console.log('📢 Системное уведомление:', data);
      this.emit('system_notification', data);
    });

    // Обновление claim статуса
    this.socket.on('claim_status_update', (data) => {
      console.log('⏰ Обновление claim статуса:', data);
      this.emit('claim_status_update', data);
    });

    // Broadcast уведомления (push-рассылки)
    this.socket.on('broadcast:new', (data) => {
      console.log('📢 Новый broadcast:', data);
      this.emit('broadcast:new', data);
    });

    // Обычные уведомления (ранги, купоны и т.д.)
    this.socket.on('notification:new', (data) => {
      console.log('🔔 Новое уведомление:', data);
      this.emit('notification:new', data);
    });

    // Обновление счетчика уведомлений
    this.socket.on('notification:count', (data) => {
      console.log('🔢 Счетчик уведомлений:', data);
      this.emit('notification:count', data);
    });
  }

  /**
   * Планирование переподключения с экспоненциальной задержкой
   */
  private scheduleReconnect(): void {
    // Очищаем предыдущий таймер
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    // Вычисляем задержку с экспоненциальным backoff (максимум 30 секунд)
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts),
      30000
    );

    console.log(`🔄 Переподключение через ${delay / 1000} сек (попытка ${this.reconnectAttempts + 1})`);

    this.reconnectTimer = setTimeout(() => {
      if (this.currentToken && !this.manualDisconnect) {
        console.log('🔄 Попытка переподключения...');
        this.connect(this.currentToken);
      }
    }, delay);
  }

  /**
   * Отключение от сервера
   */
  disconnect(): void {
    this.manualDisconnect = true;

    // Очищаем таймер переподключения
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.listeners.clear();
      console.log('WebSocket отключен вручную');
    }

    this.currentToken = null;
  }

  /**
   * Отправка события на сервер
   */
  emit(event: string, data?: any): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn('WebSocket не подключен, невозможно отправить событие:', event);
    }

    // Также вызываем локальные слушатели
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach(listener => {
        try {
          listener(data);
        } catch (error) {
          console.error('Ошибка в слушателе события:', error);
        }
      });
    }
  }

  /**
   * Подписка на событие
   */
  on(event: string, callback: Function): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    
    this.listeners.get(event)!.add(callback);

    // Возвращаем функцию для отписки
    return () => {
      const eventListeners = this.listeners.get(event);
      if (eventListeners) {
        eventListeners.delete(callback);
        if (eventListeners.size === 0) {
          this.listeners.delete(event);
        }
      }
    };
  }

  /**
   * Отписка от события
   */
  off(event: string, callback?: Function): void {
    if (callback) {
      const eventListeners = this.listeners.get(event);
      if (eventListeners) {
        eventListeners.delete(callback);
        if (eventListeners.size === 0) {
          this.listeners.delete(event);
        }
      }
    } else {
      this.listeners.delete(event);
    }
  }

  /**
   * Проверка состояния подключения
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Получение ID сокета
   */
  getSocketId(): string | undefined {
    return this.socket?.id;
  }

  /**
   * Получение экземпляра сокета для прямой подписки на события
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Отправка сообщения в чат
   */
  sendChatMessage(roomId: string, message: string): void {
    this.emit('chat_send', { roomId, message });
  }

  /**
   * Присоединение к комнате чата
   */
  joinChatRoom(roomId: string): void {
    this.emit('chat_join', { roomId });
  }

  /**
   * Выход из комнаты чата
   */
  leaveChatRoom(roomId: string): void {
    this.emit('chat_leave', { roomId });
  }

  /**
   * Запрос обновления данных
   */
  requestUpdate(type: 'balance' | 'staking' | 'claim' | 'all'): void {
    this.emit('request_update', { type });
  }
}

// Создаем синглтон
const wsService = new WebSocketService();

export default wsService;